import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CLIENT_ACTION } from "../../redux/action";
import { markServiceOnboardingCompletedInStorage } from "../../utils/clientOnboarding";
import "./index.css";

const TOUR_STEPS = [
  {
    step: 1,
    target: "nav-documents",
    path: "/finlo/client/documents",
    requiredPath: "/finlo/client/my_services",
    title: "Next Step: Upload Your Documents",
    description: "Go to the Documents section and upload the required files to start processing your service request.",
    position: "right",
  },
  {
    step: 2,
    target: "upload-files-btn",
    path: "/finlo/client/documents", // stays on documents page
    requiredPath: "/finlo/client/documents",
    title: "Upload Your Documents",
    description: "Click Upload Files to select and upload the required documents for your service request.",
    position: "bottom-left",
  },
  {
    step: 3,
    target: "nav-messages",
    path: "/finlo/client/messages", // goes to message page
    requiredPath: "/finlo/client/documents",
    title: "Go to the New Messages",
    description: "You'll receive notifications here whenever your service provider sends you a message or requests additional information.",
    position: "right",
  },
  {
    step: 4,
    target: "nav-referral",
    path: "/finlo/client/referral", // goes to Referral page
    requiredPath: "/finlo/client/messages",
    title: "Refer & Earn",
    description: "Share your referral link with friends. You'll receive rewards when they successfully join and use our services.",
    position: "right",
  },
  {
    step: 5,
    target: "nav-my_tax_team",
    path: "/finlo/client/my_tax_team", // goes to My Professionals page
    requiredPath: "/finlo/client/referral",
    title: "Meet Your Tax Team",
    description: "View the tax professionals assigned to your account and contact them whenever you need assistance.",
    position: "right",
  },
  // {
  //   step: 6,
  //   target: "nav-contact_us",
  //   path: "/finlo/client/contact_us", 
  //   requiredPath: "/finlo/client/my_tax_team",
  //   title: "Need Help?",
  //   description: "Contact our support team if you have any questions or need assistance with your service request.",
  //   position: "right",
  // },
];

const TourGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [coords, setCoords] = useState(null);
  const [bubbleCoords, setBubbleCoords] = useState({ top: 0, left: 0 });
  const bubbleRef = useRef(null);
  const resizeIntervalRef = useRef(null);

  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const isClient = userRole === "client";
  const isTourCompleted = localStorage.getItem("finlo_tour_completed") === "true";

  const getIsOnboardingCompleted = () => {
    try {
      const userDataStr = localStorage.getItem("USER_DATA");
      if (!userDataStr) return false;
      const userData = JSON.parse(userDataStr);
      const flag = userData?.service_onboarding_completed ?? userData?.serviceOnboardingCompleted;
      return flag === true || flag === 1 || String(flag).toLowerCase() === "true";
    } catch {
      return false;
    }
  };
  const isOnboardingCompleted = getIsOnboardingCompleted();

  const isForceStart =
    localStorage.getItem("finlo_tour_force_start") === "true";

  // 1. Initial State Sync & Routing enforcement
  useEffect(() => {
    if (!isClient) {
      setIsOpen(false);
      return;
    }

    // Manual restart from Help & Support bypasses completed/onboarding gates.
    if (!isForceStart && (isTourCompleted || isOnboardingCompleted)) {
      setIsOpen(false);
      return;
    }

    const savedStep = Number(localStorage.getItem("finlo_tour_step") || "1");
    setStep(savedStep);

    const currentStepConfig = TOUR_STEPS.find((s) => s.step === savedStep);
    if (!currentStepConfig) return;

    const normalizedPath = location.pathname.toLowerCase();
    const requiredPath = currentStepConfig.requiredPath.toLowerCase();

    if (normalizedPath !== requiredPath) {
      navigate(currentStepConfig.requiredPath, { replace: true });
    } else {
      setIsOpen(true);
    }
  }, [
    location.pathname,
    isClient,
    isTourCompleted,
    isOnboardingCompleted,
    isForceStart,
  ]);

  // Listen to step change event triggered externally (like upload modal closing)
  useEffect(() => {
    const handleStepChange = () => {
      const savedStep = Number(localStorage.getItem("finlo_tour_step") || "1");
      setStep(savedStep);
    };

    const handleStartPortalTour = () => {
      localStorage.removeItem("finlo_tour_completed");
      localStorage.setItem("finlo_tour_step", "1");
      localStorage.setItem("finlo_tour_force_start", "true");
      setStep(1);
      setIsOpen(false);
    };

    window.addEventListener("tour-step-change", handleStepChange);
    window.addEventListener("start-portal-tour", handleStartPortalTour);
    return () => {
      window.removeEventListener("tour-step-change", handleStepChange);
      window.removeEventListener("start-portal-tour", handleStartPortalTour);
    };
  }, []);

  // 2. Element Target Coordinates Calculation
  const calculateCoordinates = () => {
    if (!isOpen) return;

    const currentStepConfig = TOUR_STEPS.find((s) => s.step === step);
    if (!currentStepConfig) return;

    const targetEl = document.querySelector(`[data-tour="${currentStepConfig.target}"]`);
    if (!targetEl) {
      setCoords(null);
      return;
    }

    const rect = targetEl.getBoundingClientRect();
    setCoords({
      x: rect.left + window.scrollX,
      y: rect.top + window.scrollY,
      width: rect.width,
      height: rect.height,
      rect,
    });
  };

  useEffect(() => {
    calculateCoordinates();

    window.addEventListener("resize", calculateCoordinates);
    window.addEventListener("scroll", calculateCoordinates, true);

    resizeIntervalRef.current = setInterval(calculateCoordinates, 300);

    return () => {
      window.removeEventListener("resize", calculateCoordinates);
      window.removeEventListener("scroll", calculateCoordinates, true);
      if (resizeIntervalRef.current) clearInterval(resizeIntervalRef.current);
    };
  }, [isOpen, step, location.pathname]);

  // 3. Bubble Position Placement Calculation
  useEffect(() => {
    if (!coords || !bubbleRef.current) return;

    const currentStepConfig = TOUR_STEPS.find((s) => s.step === step);
    if (!currentStepConfig) return;

    const bubbleRect = bubbleRef.current.getBoundingClientRect();
    let top = 0;
    let left = 0;

    const margin = 12;

    switch (currentStepConfig.position) {
      case "right":
        top = coords.y + (coords.height / 2) - (bubbleRect.height / 2);
        left = coords.x + coords.width + margin;
        break;
      case "bottom-left":
        top = coords.y + coords.height + margin;
        left = coords.x + coords.width - bubbleRect.width;
        break;
      default:
        top = coords.y + coords.height + margin;
        left = coords.x;
    }

    if (left < 10) left = 10;
    if (left + bubbleRect.width > window.innerWidth - 10) {
      left = window.innerWidth - bubbleRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + bubbleRect.height > window.innerHeight - 10) {
      top = window.innerHeight - bubbleRect.height - 10;
    }

    setBubbleCoords({ top, left });
  }, [coords, step]);

  // 4. Mutual click handler (advances step when target element is clicked directly)
  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentClick = (e) => {
      const currentStepConfig = TOUR_STEPS.find((s) => s.step === step);
      if (!currentStepConfig) return;

      const clickedTourTarget = e.target.closest("[data-tour]");
      if (!clickedTourTarget) return;

      const tourKey = clickedTourTarget.getAttribute("data-tour");

      // Direct target hit
      if (tourKey === currentStepConfig.target) {
        handleNextStep();
        return;
      }

      // Next step target hit (allows clicking next sidebar tab directly)
      const nextStepConfig = TOUR_STEPS.find((s) => s.step === step + 1);
      if (nextStepConfig && tourKey === nextStepConfig.target) {
        handleNextStep();
        return;
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [isOpen, step]);

  const handleNextStep = () => {
    const currentStepConfig = TOUR_STEPS.find((s) => s.step === step);
    if (!currentStepConfig) return;

    const nextStep = step + 1;
    if (nextStep > TOUR_STEPS.length) {
      completeTour(currentStepConfig.path);
    } else {
      localStorage.setItem("finlo_tour_step", nextStep.toString());
      setStep(nextStep);
      navigate(currentStepConfig.path);
    }
  };

  const completeTour = async (redirectPath) => {
    localStorage.setItem("finlo_tour_completed", "true");
    localStorage.removeItem("finlo_tour_step");
    localStorage.removeItem("finlo_tour_force_start");
    setIsOpen(false);

    // Call API and update local storage to complete service onboarding
    const clientId = localStorage.getItem("user_id");
    if (clientId && !isOnboardingCompleted) {
      try {
        await CLIENT_ACTION.completeServiceOnboarding(clientId);
        markServiceOnboardingCompletedInStorage();
      } catch (err) {
        console.error("Failed to complete onboarding:", err);
      }
    }

    if (redirectPath) {
      navigate(redirectPath);
    } else {
      const userRole = (localStorage.getItem("user_role") || "client").toLowerCase();
      navigate(`/finlo/${userRole}/dashboard`);
    }
  };

  const handleSkipTour = () => {
    completeTour();
  };

  if (!isOpen || !coords) return null;

  const currentStepConfig = TOUR_STEPS.find((s) => s.step === step);
  if (!currentStepConfig) return null;

  const cutoutPadding = 6;

  return (
    <>
      {/* <svg
        className="tour-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 999990,
          pointerEvents: "auto",
        }}
      >
        <mask id="tour-spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect
            x={coords.x - window.scrollX - cutoutPadding}
            y={coords.y - window.scrollY - cutoutPadding}
            width={coords.width + cutoutPadding * 2}
            height={coords.height + cutoutPadding * 2}
            rx={8}
            ry={8}
            fill="black"
          />
        </mask>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.45)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg> */}

      <div
        className="tour-highlight-border"
        style={{
          position: "fixed",
          top: coords.y - window.scrollY - cutoutPadding,
          left: coords.x - window.scrollX - cutoutPadding,
          width: coords.width + cutoutPadding * 2,
          height: coords.height + cutoutPadding * 2,
          zIndex: 999992,
          pointerEvents: "none",
        }}
      />

      <div
        className="tour-click-interceptor"
        style={{
          position: "fixed",
          top: coords.y - window.scrollY - cutoutPadding,
          left: coords.x - window.scrollX - cutoutPadding,
          width: coords.width + cutoutPadding * 2,
          height: coords.height + cutoutPadding * 2,
          zIndex: 999995,
          cursor: "pointer",
        }}
        onClick={(e) => {
          const targetEl = document.querySelector(`[data-tour="${currentStepConfig.target}"]`);
          if (targetEl) {
            targetEl.click();
          }
        }}
      />

      <div
        ref={bubbleRef}
        className={`tour-bubble bubble-pos-${currentStepConfig.position}`}
        style={{
          position: "fixed",
          top: bubbleCoords.top - window.scrollY,
          left: bubbleCoords.left - window.scrollX,
          zIndex: 999999,
        }}
      >
        <div className="tour-bubble-arrow" />
        <div className="tour-bubble-content">
          <h4 className="tour-bubble-title">{currentStepConfig.title}</h4>
          <p className="tour-bubble-description">{currentStepConfig.description}</p>
          <div className="tour-bubble-actions">
            <button
              type="button"
              className="tour-skip-button"
              onClick={handleSkipTour}
            >
              Skip Tour
            </button>
            <button
              type="button"
              className="tour-arrow-button"
              onClick={handleNextStep}
              aria-label="Next Step"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1C75BC"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TourGuide;
